import React from "react";
import {
  PageStyles,
  NewPageTop,
  NewCentralArea,
} from "iebt-zing-storybook";
import { EspacoTeiaNewSidebar } from "components/branding/EspacoTeiaNewSidebar";
import { Outlet, useLocation } from "react-router";
import { Breadcrumb } from "components/Breadcrumb";
import { BreadcrumbContainer } from "components/Breadcrumb/UI/style";
import { RoleSelector } from "components/RoleSelector";
import { trans } from "sharedKernel/i18n/translate";
import { NewZingMenu } from "components/menu/side/menu/NewZingMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useInnerPageHooks } from "./useInnerPageHooks";
import { useMobileSidebarCloseOnMenuClick } from "./useMobileSidebarCloseOnMenuClick";
import {
  TeiaInnerPage,
  SidebarBackdrop,
  MobileHamburgerWrap,
  MobileHamburger,
  MobileSidebarCloseBar,
  MobileSidebarCloseButton,
  TeiaMobileSidebarGlobalStyles,
} from "./InnerPage.styles";

export interface InnerPageProps {
  pageTitle?: string;
}

export const InnerPage = ({ ...props }: InnerPageProps): JSX.Element => {
  const location = useLocation();

  const {
    user,
    isSidebarOpen,
    setIsSidebarOpen,
    isSuperAdmin,
    isBehavingAs,
    handleBackToCompanySelection,
    menuItems,
    filterText,
    handleFilterChange,
    filteredCompanies,
    handleCompanySelect,
    selectedProfileName,
    menuTopItems,
    isCaixaUser,
    selectedRole,
    setSelectedRole,
    isFormPage,
    forceRenderKey
  } = useInnerPageHooks();

  useMobileSidebarCloseOnMenuClick(setIsSidebarOpen);

  const centralAreaContent = (
    <>
      {!location.pathname.includes("/hub/company/calendar") &&
        !location.pathname.includes("/hub/dashboard") && (
          <BreadcrumbContainer>
            <Breadcrumb />
          </BreadcrumbContainer>
        )}
      {<Outlet />}
    </>
  );

  return (
    <TeiaInnerPage>
      <TeiaMobileSidebarGlobalStyles />
      <SidebarBackdrop
        $visible={isSidebarOpen}
        onClick={() => setIsSidebarOpen(false)}
        onKeyDown={(e) => e.key === "Escape" && setIsSidebarOpen(false)}
        role="presentation"
        aria-hidden={!isSidebarOpen}
      />
      <MobileSidebarCloseBar $open={isSidebarOpen}>
        <MobileSidebarCloseButton
          type="button"
          onClick={() => setIsSidebarOpen(false)}
          aria-label={trans("Fechar menu")}
          aria-hidden={!isSidebarOpen}
          tabIndex={isSidebarOpen ? 0 : -1}
        >
          <FontAwesomeIcon icon={["fas", "times"]} />
        </MobileSidebarCloseButton>
      </MobileSidebarCloseBar>
      <PageStyles {...props} key={forceRenderKey}>
        <EspacoTeiaNewSidebar
          isSuperAdmin={user.isSuperAdmin}
          user={user}
          isOpen={isSidebarOpen}
          isOpenHandler={setIsSidebarOpen}
          isTeiaHeader={true}
          gradientStartColor="#00599C"
          gradientStartPercent={9.13}
          gradientEndColor="#0066B3"
          gradientEndPercent={100}
        >
          {isSuperAdmin ? (
            <>
              <div style={{ display: isBehavingAs ? "block" : "none" }}>
                <div
                  role="button"
                  aria-label="Voltar"
                  onClick={() => handleBackToCompanySelection()}
                  style={{
                    width: "32px",
                    height: "32px",
                    flexShrink: 0,
                    aspectRatio: "1 / 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#0066B3",
                    cursor: "pointer",
                  }}
                >
                  <FontAwesomeIcon
                    icon={["fas", "arrow-left"]}
                    style={{ width: "32px", height: "32px", color: "white" }}
                  />
                </div>
          <NewZingMenu
              isTeiaHeader={true}
              menuItems={menuItems}
              isSidebarOpen={isSidebarOpen}
            />
          </div>

          <div style={{ display: isBehavingAs ? "none" : "block" }}>
                <div style={{ padding: "1rem", position: "relative" }}>
                  <div style={{ position: "fixed" }}>
                    <input
                      type="text"
                      placeholder={trans("Buscar empresas...")}
                      value={filterText}
                      onChange={handleFilterChange}
                      style={{
                        width: "100%",
                        padding: "0.75rem 0.75rem 0.75rem 2.5rem",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "0.9rem",
                        backgroundColor: "white",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                    <FontAwesomeIcon
                      icon={["fas", "magnifying-glass"]}
                      style={{
                        position: "absolute",
                        left: "0.75rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#999",
                        fontSize: "0.9rem",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                </div>
                <NewZingMenu
                  isTeiaHeader={true}
                  menuItems={filteredCompanies || []}
                  isSidebarOpen={isSidebarOpen}
                  onItemClick={handleCompanySelect}
                />
              </div>
            </>
          ) : (
            <NewZingMenu
              isTeiaHeader={true}
              menuItems={menuItems}
              isSidebarOpen={isSidebarOpen}
            />
          )}
        </EspacoTeiaNewSidebar>
        <NewPageTop
          isSuperAdmin={user.isSuperAdmin}
          multiUserName={selectedProfileName}
          userAvatar={user.avatar}
          isSidebarOpen={isSidebarOpen}
          sidebarToggleHandler={setIsSidebarOpen}
          menuContent={menuTopItems}
        />
        <div style={{ position: "relative" }}>
          <NewPageTop
            userAvatar={user.avatar}
            isSidebarOpen={isSidebarOpen}
            sidebarToggleHandler={setIsSidebarOpen}
            menuContent={menuTopItems}
          >
            <MobileHamburgerWrap>
              <MobileHamburger
                type="button"
                onClick={() => setIsSidebarOpen((o) => !o)}
                aria-label={trans("Abrir menu")}
                aria-expanded={isSidebarOpen}
              >
                <FontAwesomeIcon icon={["fas", "bars"]} />
              </MobileHamburger>
            </MobileHamburgerWrap>
            {(isCaixaUser && !isSuperAdmin) && (
              <div
                style={{
                  position: "absolute",
                  top: "23px",
                  left: "50px",
                  zIndex: 1001,
                }}
              >
                <RoleSelector
                  selectedRole={selectedRole}
                  onRoleChange={setSelectedRole}
                  className="role-selector-header"
                />
              </div>
            )}
          </NewPageTop>
        </div>
        <NewCentralArea isOpen={isSidebarOpen} isFormPage={isFormPage}>
          {centralAreaContent}
        </NewCentralArea>
      </PageStyles>
    </TeiaInnerPage>
  );
};
